import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Button, Collapse, LinearProgress, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material'
import { H3 } from '../../layouts/TextStyles'
import { tone, ui } from '../../utils/uiTokens'
import { capitalize } from '../../utils/functions'
import { useAppSelector } from '../../app/hooks'
import { SUBJECT_HEAD } from '../../utils/constants'
import {
    SURVEY_TYPES, SurveyAnswerRow, SurveyMentorResult, SurveyQuestion, SurveyType, downloadSurveyExcel, surveyApi,
} from '../../features/survey/surveyApi'
import { Kpi, PctPill, bodyRow, btn, headRow, kpiRow, muted, page, sub, tableWrap } from '../director/ui'
import { TypeTabs } from './SurveysPage'
import { addMonths, ddmm } from './surveyTemplates'

const outlined = { ...btn, color: ui.ink, borderColor: ui.border }
const qText = (q: SurveyQuestion, L: string) => (q as any)[`text${L}`] || q.textRu || ''

// Результаты опроса по оцениваемым. Ни имён, ни учеников: «Жауап №» по порядку анкет.
const SurveyResultsPage = () => {
    const { formatMessage, locale } = useIntl()
    const L = capitalize(locale)
    const role = useAppSelector((s) => s.user.userInfo.roles[0])
    // пән басшы видит только опросы о менторах и учителях своей зоны; фильтр и на бэкенде
    const types: SurveyType[] = role === SUBJECT_HEAD ? ['MENTOR', 'TEACHER'] : SURVEY_TYPES
    const [params, setParams] = useSearchParams()
    const type = types.find((t) => t === params.get('type')) ?? types[0]
    const { data: list } = surveyApi.useGetSurveysQuery({ type })
    const surveys = [...(list?.surveys ?? [])].sort((a, b) => (b.openDate ?? '').localeCompare(a.openDate ?? ''))
    const surveyId = Number(params.get('surveyId')) || surveys.find((s) => s.responseCount > 0)?.id || surveys[0]?.id || 0

    const { data, isFetching } = surveyApi.useGetSurveyResultsQuery(surveyId, { skip: !surveyId })
    const [apply, { isLoading: applying }] = surveyApi.useApplySurveyMutation()
    const [openRow, setOpenRow] = useState<string | null>(null)
    const [loadingXls, setLoadingXls] = useState<string | null>(null)

    const current = data && data.survey.id === surveyId ? data : undefined
    const sType: SurveyType = current?.survey.type ?? type
    const mentors = current?.mentors ?? []
    const month = current?.survey.monthNumber
    const responses = mentors.reduce((s, m) => s + m.responses, 0)
    const students = mentors.reduce((s, m) => s + m.students, 0)
    const rated = mentors.filter((m) => m.percent != null)
    const avg = rated.length ? Math.round(rated.reduce((s, m) => s + (m.percent ?? 0), 0) / rated.length) : null
    const showApply = sType === 'MENTOR' && !!current?.canApply
    const cols = 4 + (sType === 'MENTOR' ? 1 : 0)

    const name = (m: SurveyMentorResult) => `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || `#${m.mentorUserId}`
    const key = (m: SurveyMentorResult) => `${m.mentorUserId}:${m.courseSubjectId}`

    const onApply = async (m: SurveyMentorResult) => {
        if (!m.mentorUserId) return
        try {
            const r = await apply({ surveyId, mentorUserId: m.mentorUserId, courseSubjectId: m.courseSubjectId }).unwrap()
            toast.success(formatMessage({ id: 'surveyApplied' }, { percent: r.percent, month: r.cycleNumber }))
        } catch { /* общий обработчик */ }
    }

    const excel = async (targetId?: number | null) => {
        const k = String(targetId ?? 'all')
        setLoadingXls(k)
        try {
            await downloadSurveyExcel(surveyId, targetId)
        } catch {
            toast.error(formatMessage({ id: 'surveyExcelError' }))
        } finally {
            setLoadingXls(null)
        }
    }

    return (
        <Box sx={{ margin: '10px' }}>
            <Box sx={page}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <H3>{formatMessage({ id: 'surveyResultsTitle' })}</H3>
                        <Box sx={{ ...sub, margin: '6px 0 16px', maxWidth: 760 }}>
                            {formatMessage({ id: role === SUBJECT_HEAD ? 'surveyResultsSubHead' : `surveyResultsSub_${type}` })}
                        </Box>
                    </Box>
                    {!!surveyId && (
                        <Button variant="outlined" sx={outlined} disabled={!!loadingXls} onClick={() => excel()}>
                            {formatMessage({ id: 'surveyExcel' })}
                        </Button>
                    )}
                </Box>

                <TypeTabs types={types} value={type} onChange={(t) => { setOpenRow(null); setParams({ type: t }, { replace: true }) }} />

                <TextField size="small" select sx={{ minWidth: { xs: '100%', sm: 420 }, maxWidth: '100%', margin: '4px 0 16px', '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                    label={formatMessage({ id: 'surveysTitle' })} value={surveys.some((s) => s.id === surveyId) ? surveyId : ''}
                    onChange={(e) => { setOpenRow(null); setParams({ type, surveyId: String(e.target.value) }, { replace: true }) }}>
                    {surveys.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                            {(s as any)[`title${L}`] || s.titleRu}
                            {(s as any)[`courseTitle${L}`] ? ` · ${(s as any)[`courseTitle${L}`]}` : ''}
                            {s.monthNumber ? ` · ${s.monthNumber} ${formatMessage({ id: 'month' }).toLowerCase()}` : ''}
                            {` · ${formatMessage({ id: 'surveyAnswersN' }, { n: s.responseCount })}`}
                        </MenuItem>
                    ))}
                </TextField>
                {!surveys.length && list && <Box sx={{ ...muted, padding: '16px 0' }}>{formatMessage({ id: 'surveyNoneOfType' })}</Box>}

                {isFetching && <LinearProgress />}

                {current && sType === 'NPS' && <NpsBlock m={mentors[0]} questions={current.survey.questions} closeDate={current.survey.closeDate} />}

                {current && sType !== 'NPS' && (
                    <>
                        <Box sx={kpiRow}>
                            <Kpi label={formatMessage({ id: sType === 'SUBJECT_HEAD' ? 'surveyKpiResponsesMentors' : 'surveyKpiResponses' })} value={`${responses} / ${students}`} />
                            <Kpi label={formatMessage({ id: `surveyTargets_${sType}` })} value={mentors.length} />
                            <Kpi label={formatMessage({ id: 'surveyKpiAvg' })} value={avg == null ? '-' : `${avg}%`} dark />
                        </Box>

                        {showApply && !month && <Box sx={{ ...muted, marginBottom: '10px' }}>{formatMessage({ id: 'surveyNoMonth' })}</Box>}
                        {!responses && <Box sx={{ ...muted, marginBottom: '10px' }}>{formatMessage({ id: 'surveyNoAnswersYet' })}</Box>}

                        <Box sx={tableWrap}>
                            <Table sx={{ minWidth: 760 }}>
                                <TableHead sx={headRow}>
                                    <TableRow>
                                        <TableCell>{formatMessage({ id: `surveyTarget_${sType}` })}</TableCell>
                                        <TableCell align="right">{formatMessage({ id: sType === 'SUBJECT_HEAD' ? 'surveyColAnsweredMentors' : 'surveyColAnswered' })}</TableCell>
                                        <TableCell align="center">{formatMessage({ id: 'surveyColPercent' })}</TableCell>
                                        {sType === 'MENTOR' && (
                                            <TableCell align="center">
                                                {formatMessage({ id: 'dirSurveyCol' })}
                                                {month ? <Box sx={{ textTransform: 'none', fontWeight: 500 }}>{month} {formatMessage({ id: 'month' }).toLowerCase()}</Box> : null}
                                            </TableCell>
                                        )}
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mentors.map((m) => {
                                        const k = key(m)
                                        const open = openRow === k
                                        const same = m.percent != null && m.appliedPercent === m.percent
                                        return (
                                            <React.Fragment key={k}>
                                                <TableRow hover sx={bodyRow}>
                                                    <TableCell>
                                                        <b>{name(m)}</b>
                                                        <Box sx={muted}>{(m as any)[`subjectTitle${L}`] || m.subjectTitleRu}</Box>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{m.responses} / {m.students}</TableCell>
                                                    <TableCell align="center">{m.percent == null ? <span style={{ color: ui.muted }}>-</span> : <PctPill value={m.percent} good={85} warn={65} />}</TableCell>
                                                    {sType === 'MENTOR' && (
                                                        <TableCell align="center">{m.appliedPercent == null ? <span style={{ color: ui.muted }}>-</span> : `${m.appliedPercent}%`}</TableCell>
                                                    )}
                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                        <Box sx={{ display: 'inline-flex', gap: 1 }}>
                                                            {m.responses > 0 && (
                                                                <Button size="small" variant="outlined" sx={outlined} onClick={() => setOpenRow(open ? null : k)}>
                                                                    {formatMessage({ id: open ? 'surveyCollapse' : 'surveyExpand' })} {open ? '▴' : '▾'}
                                                                </Button>
                                                            )}
                                                            {showApply && (
                                                                <Button size="small" variant="outlined" sx={{ ...btn, color: same ? ui.muted : ui.pink, borderColor: same ? ui.border : ui.pinkSoft }}
                                                                    disabled={applying || m.percent == null || !month || same} onClick={() => onApply(m)}>
                                                                    {formatMessage({ id: same ? 'surveyAppliedShort' : 'surveyApply' })}
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={cols} sx={{ padding: 0, border: open ? undefined : 'none' }}>
                                                        <Collapse in={open} unmountOnExit>
                                                            <TargetDetail m={m} questions={current.survey.questions}
                                                                excelBusy={!!loadingXls} onExcel={() => excel(m.mentorUserId)} type={sType} />
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        )
                                    })}
                                    {!isFetching && !mentors.length && (
                                        <TableRow sx={bodyRow}>
                                            <TableCell colSpan={cols} align="center" sx={{ color: ui.muted }}>{formatMessage({ id: 'surveyNoMentors' })}</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    )
}

const h4 = { fontSize: '12px', fontWeight: 700, color: ui.sub, textTransform: 'uppercase' as const, letterSpacing: '0.03em', margin: '0 0 10px' }

const Bar: React.FC<{ value: number; max: number; label: string }> = ({ value, max, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginTop: '6px' }}>
        <Box sx={{ flex: 1, maxWidth: 320, height: 7, borderRadius: 999, background: ui.track }}>
            <Box sx={{ height: '100%', borderRadius: 999, background: ui.pink, width: `${(value / max) * 100}%` }} />
        </Box>
        <Box sx={{ fontSize: '13px', color: ui.sub, whiteSpace: 'nowrap' }}>{label}</Box>
    </Box>
)

const cell = (row: SurveyAnswerRow, qid?: number | null) => row.answers.find((a) => a.questionId === qid)
const studentName = (row: SurveyAnswerRow) => `${row.lastName ?? ''} ${row.firstName ?? ''}`.trim()

const TargetDetail: React.FC<{ m: SurveyMentorResult; questions: SurveyQuestion[]; type: SurveyType; excelBusy: boolean; onExcel: () => void }> = ({
    m, questions, type, excelBusy, onExcel,
}) => {
    const { formatMessage, locale } = useIntl()
    const L = capitalize(locale)
    const nums = questions.map((q, i) => ({ q, n: i + 1 }))
    const rating = nums.filter((x) => x.q.kind === 'RATING' || x.q.kind === 'NPS')
    const choice = nums.filter((x) => x.q.kind === 'CHOICE')
    const texts = nums.filter((x) => x.q.kind === 'TEXT')
    const rows = m.answerRows ?? []
    const dash = <span style={{ color: ui.muted }}>–</span>

    return (
        <Box sx={{ padding: '14px 16px 16px', background: ui.surface, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
                <Box sx={h4}>{formatMessage({ id: 'surveyAvgByQuestion' })}</Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {nums.filter((x) => x.q.kind !== 'TEXT').map(({ q, n }) => {
                        const r = m.questions.find((x) => x.questionId === q.id)
                        return (
                            <Box key={q.id ?? n}>
                                <Box sx={{ fontSize: '13.5px', fontWeight: 600, color: ui.ink }}>{n}. {qText(q, L)}</Box>
                                {q.kind === 'RATING' && (
                                    <Bar value={r?.average ?? 0} max={5}
                                        label={`${r?.average != null ? `${r.average.toFixed(1)} / 5` : '-'} · ${formatMessage({ id: 'surveyAnswersN' }, { n: r?.answered ?? 0 })}`} />
                                )}
                                {q.kind === 'NPS' && (
                                    <Box sx={{ ...sub, marginTop: '4px' }}>NPS: <b style={{ color: ui.ink }}>{r?.nps ?? '-'}</b></Box>
                                )}
                                {q.kind === 'CHOICE' && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: '6px' }}>
                                        {q.options.map((o, k) => (
                                            <Box key={k} sx={{ fontSize: '13px', color: ui.sub }}>
                                                {(o as any)[`text${L}`] || o.textRu}: <b style={{ color: ui.ink }}>{r?.choiceCounts?.[k] ?? 0}</b>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )
                    })}
                </Box>
            </Box>

            <Box>
                <Box sx={h4}>{formatMessage({ id: `surveyEachAnswer_${type}` })}</Box>
                <Box sx={{ ...tableWrap, background: ui.card }}>
                    <Table size="small" sx={{ minWidth: 600 }}>
                        <TableHead sx={headRow}>
                            <TableRow>
                                <TableCell>{formatMessage({ id: 'surveyAnswerNo' })}</TableCell>
                                {rows.some((r) => r.studentUserId) && <TableCell>{formatMessage({ id: 'surveyStudent' })}</TableCell>}
                                {rating.map(({ q, n }) => <TableCell key={n} align="center" title={qText(q, L)}>{formatMessage({ id: 'surveyQn' }, { n })}</TableCell>)}
                                {rating.some((x) => x.q.kind === 'RATING') && <TableCell align="center">{formatMessage({ id: 'surveyAvg' })}</TableCell>}
                                {choice.map(({ q, n }) => <TableCell key={n} title={qText(q, L)}>{formatMessage({ id: 'surveyQn' }, { n })}</TableCell>)}
                                {texts.map(({ q, n }) => <TableCell key={n} title={qText(q, L)}>{formatMessage({ id: 'surveyQnText' }, { n })}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => {
                                const vals = rating.filter((x) => x.q.kind === 'RATING').map(({ q }) => cell(row, q.id)?.rating).filter((v): v is number => v != null)
                                return (
                                    <TableRow key={row.number} sx={bodyRow}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>№{row.number}</TableCell>
                                        {rows.some((r) => r.studentUserId) && (
                                            <TableCell sx={{ minWidth: 160 }}>{studentName(row) || (row.studentUserId ? `#${row.studentUserId}` : dash)}</TableCell>
                                        )}
                                        {rating.map(({ q, n }) => {
                                            const a = cell(row, q.id)
                                            const v = q.kind === 'NPS' ? a?.nps : a?.rating
                                            return <TableCell key={n} align="center">{v == null ? dash : <b>{v}</b>}</TableCell>
                                        })}
                                        {rating.some((x) => x.q.kind === 'RATING') && (
                                            <TableCell align="center">{vals.length ? <b>{(vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1)}</b> : dash}</TableCell>
                                        )}
                                        {choice.map(({ q, n }) => {
                                            const c = cell(row, q.id)?.choice
                                            const o = c ? q.options[c - 1] : null
                                            return <TableCell key={n}>{o ? (o as any)[`text${L}`] || o.textRu : dash}</TableCell>
                                        })}
                                        {texts.map(({ q, n }) => {
                                            const t = cell(row, q.id)?.text?.trim()
                                            return <TableCell key={n} sx={{ minWidth: 220, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{t || dash}</TableCell>
                                        })}
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </Box>
            </Box>

            {texts.map(({ q, n }) => (
                <TextCarousel key={n} title={`${n}. ${qText(q, L)}`}
                    items={rows.map((r) => ({ no: r.number, text: cell(r, q.id)?.text?.trim() ?? '', extra: studentName(r) || undefined })).filter((x) => x.text)} />
            ))}

            <Box>
                <Button size="small" variant="outlined" sx={outlined} disabled={excelBusy} onClick={onExcel}>
                    {formatMessage({ id: 'surveyExcelTarget' })}
                </Button>
            </Box>
        </Box>
    )
}

const TextCarousel: React.FC<{ title: string; items: { no: number; text: string; extra?: string }[] }> = ({ title, items }) => {
    const { formatMessage } = useIntl()
    const [i, setI] = useState(0)
    const idx = Math.min(i, Math.max(0, items.length - 1))
    const it = items[idx]
    return (
        <Box>
            <Box sx={{ fontSize: '13.5px', fontWeight: 600, color: ui.ink, marginBottom: '6px' }}>{title}</Box>
            {!it ? (
                <Box sx={muted}>{formatMessage({ id: 'surveyNoTexts' })}</Box>
            ) : (
                <Box sx={{ background: ui.card, border: `1px solid ${ui.border}`, borderRadius: ui.radius, padding: '12px 14px' }} aria-live="polite">
                    <Box sx={{ fontSize: '14px', color: ui.ink, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>«{it.text}»</Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, marginTop: '10px', flexWrap: 'wrap' }}>
                        <Box sx={muted}>
                            {formatMessage({ id: 'surveyCarouselPos' }, { i: idx + 1, n: items.length, no: it.no })}
                            {it.extra ? ` · ${it.extra}` : ''}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" variant="outlined" sx={outlined} disabled={idx === 0} onClick={() => setI(idx - 1)}>
                                ‹ {formatMessage({ id: 'surveyPrev' })}
                            </Button>
                            <Button size="small" variant="outlined" sx={outlined} disabled={idx >= items.length - 1} onClick={() => setI(idx + 1)}>
                                {formatMessage({ id: 'surveyNext' })} ›
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    )
}

// NPS = % ұсынушылар (9-10) − % сынаушылар (0-6)
const NpsBlock: React.FC<{ m: SurveyMentorResult | undefined; questions: SurveyQuestion[]; closeDate: string | null }> = ({ m, questions, closeDate }) => {
    const { formatMessage, locale } = useIntl()
    const L = capitalize(locale)
    const npsQ = questions.find((q) => q.kind === 'NPS')
    const textQs = questions.map((q, i) => ({ q, n: i + 1 })).filter((x) => x.q.kind === 'TEXT')
    const rows = m?.answerRows ?? []
    const scores = rows.map((r) => cell(r, npsQ?.id)?.nps).filter((v): v is number => v != null)
    const n = scores.length

    if (!n) {
        return (
            <>
                <Box sx={kpiRow}><Kpi label={formatMessage({ id: 'surveyKpiAnswered' })} value={0} /></Box>
                <Box sx={muted}>{formatMessage({ id: 'surveyNoAnswersYet' })}</Box>
            </>
        )
    }

    const det = scores.filter((s) => s <= 6).length
    const pas = scores.filter((s) => s >= 7 && s <= 8).length
    const pd = Math.round((det / n) * 100)
    const pp = Math.round((pas / n) * 100)
    const ppr = 100 - pd - pp
    const pro = n - det - pas
    const score = Math.round(((pro - det) / n) * 100)
    const hist = Array.from({ length: 11 }, (_, k) => scores.filter((s) => s === k).length)
    const mx = Math.max(...hist, 1)
    const col = (k: number) => (k <= 6 ? tone.bad.text : k <= 8 ? tone.warn.text : tone.ok.text)
    const seg = (w: number, bg: string) => (
        <Box sx={{ width: `${w}%`, background: bg, color: ui.card, fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {w >= 8 ? `${w}%` : ''}
        </Box>
    )
    const legend = (bg: string, id: string) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Box component="i" sx={{ width: 10, height: 10, borderRadius: '3px', background: bg, display: 'inline-block' }} />
            {formatMessage({ id })}
        </Box>
    )

    return (
        <>
            <Box sx={kpiRow}>
                <Kpi label={formatMessage({ id: 'surveyKpiAnswered' })} value={n} />
                <Kpi label={formatMessage({ id: 'surveyNpsPromoters' })} value={`${ppr}%`} />
                <Kpi label={formatMessage({ id: 'surveyNpsDetractors' })} value={`${pd}%`} />
                <Kpi label="NPS" value={`${score > 0 ? '+' : ''}${score}`} dark />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, marginBottom: '22px' }}>
                <Box>
                    <Box sx={h4}>{formatMessage({ id: 'surveyNpsShare' })}</Box>
                    <Box sx={{ display: 'flex', height: 28, borderRadius: ui.radius, overflow: 'hidden', background: ui.track }}>
                        {seg(pd, tone.bad.text)}
                        {seg(pp, tone.warn.text)}
                        {seg(ppr, tone.ok.text)}
                    </Box>
                    <Box sx={{ ...muted, display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: '8px' }}>
                        {legend(tone.bad.text, 'surveyNpsLegendDet')}
                        {legend(tone.warn.text, 'surveyNpsLegendPas')}
                        {legend(tone.ok.text, 'surveyNpsLegendPro')}
                    </Box>
                    <Box sx={{ ...muted, marginTop: '10px' }}>
                        NPS = {ppr}% − {pd}% = {score}.
                        {closeDate ? ` ${formatMessage({ id: 'surveyNpsNext' }, { date: ddmm(addMonths(closeDate, 3)) })}` : ''}
                    </Box>
                </Box>
                <Box>
                    <Box sx={h4}>{formatMessage({ id: 'surveyNpsHist' })}</Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: 120 }}>
                        {hist.map((c, k) => (
                            <Box key={k} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                                <Box sx={{ ...muted, fontSize: '11px' }}>{c}</Box>
                                <Box sx={{ width: '100%', borderRadius: '4px 4px 0 0', background: col(k), height: `${(c / mx) * 80}%`, minHeight: c ? 2 : 0 }} />
                                <Box sx={{ ...muted, fontSize: '11px' }}>{k}</Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
            {textQs.map(({ q, n: qn }) => (
                <TextCarousel key={qn} title={`${qn}. ${qText(q, L)}`}
                    items={rows
                        .map((r) => ({ no: r.number, text: cell(r, q.id)?.text?.trim() ?? '', s: cell(r, npsQ?.id)?.nps }))
                        .filter((x) => x.text)
                        .map((x) => ({ no: x.no, text: x.text, extra: x.s != null ? formatMessage({ id: 'surveyNpsScore' }, { s: x.s }) : undefined }))} />
            ))}
        </>
    )
}

export default SurveyResultsPage
